<template>
  <div class="row _margin-bottom-30">
    <label>Your Address</label>
    <div class="display-data _margin-bottom-20">
      {{ quote.address | objectToCsv(['addressLine1', 'addressLine2', 'town', 'county', 'postCode']) }}
    </div>

    <label>Payment Method</label>
    <div v-if="quote.switchType == 'dualDistinctToDualConsolidated'">
      <p class="_margin-top-0 _margin-bottom-0"><small>Gas</small></p>
      <payment-type-display :payment-id="quote.gasCurrentPayMethod"
        @onEdit="onEdit('paymentType', 'gasCurrentPayMethod')" />

      <p class="_margin-top-0 _margin-bottom-0"><small>Electricity</small></p>
      <payment-type-display :payment-id="quote.electricityCurrentPayMethod"
        @onEdit="onEdit('paymentType', 'electricityCurrentPayMethod')" />
    </div>

    <div class="_margin-top-10" v-else>
      <payment-type-display :payment-id="quote.currentPayMethod" @onEdit="onEdit('paymentType', 'currentPayMethod')" />
    </div>

    <label>Supplier & Tariff</label>
    <div v-if="quote.compareFuel == 'gasElectricity' && quote.switchType !== 'dualConsolidatedToDualConsolidated'">
      <p class="_margin-top-0 _margin-bottom-0"><small>Gas</small></p>
      <supplier-tariff-display :supplier-id="quote.gasSupplierId" :supplier-name="quote.gasSupplierName"
        :tariff-name="quote.gasTariffName" @onEdit="onEdit('supplierTariff', 'gasSupplierId')" />

      <p class="_margin-top-0 _margin-bottom-0"><small>Electricity</small></p>
      <supplier-tariff-display :supplier-id="quote.electricitySupplierId" :supplier-name="quote.electricitySupplierName"
        :tariff-name="quote.electricityTariffName" @onEdit="onEdit('supplierTariff', 'electricitySupplierId')" />
    </div>

    <div class="_margin-top-10" v-else>
      <supplier-tariff-display :supplier-id="quote.supplierId" :supplier-name="quote.supplierName"
        :tariff-name="quote.tariffName" @onEdit="onEdit('supplierTariff', 'supplierId')" />
    </div>

    <hr />

    <label>Your Usage</label>

    <div v-if="quote.compareFuel == 'gasElectricity'">
      <p class="_margin-top-0 _margin-bottom-0"><small>Electricity Consumption</small></p>
      <usage-display :value="quote.electricityCurrentUsageEstimated"
        @onEdit="onEditUsage('electricityCurrentUsageEstimated')" />

      <p class="_margin-bottom-0"><small>Gas Consumption</small></p>
      <usage-display :value="quote.gasCurrentUsageEstimated" @onEdit="onEditUsage('gasCurrentUsageEstimated')" />
    </div>

    <div class="_margin-top-10" v-else>
      <p class="_margin-top-0 _margin-bottom-0"><small>{{ quote.compareFuel | capitalize }} Consumption</small></p>
      <usage-display :value="quote.currentUsageEstimated" @onEdit="onEditUsage('currentUsageEstimated')" />
    </div>

    <uiv-alert type="danger" v-if="$v.form.$error">
      <b>Error</b> <br />
      Please ensure you answered all required fields
    </uiv-alert>

    <uiv-alert type="danger" v-if="error">
      <b>Error</b> <br />
      {{ error }}
    </uiv-alert>

    <div class="sticky-footer">
      <uiv-btn type="primary" block @click="onSubmit">Get a quote</uiv-btn>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  required, requiredIf, numeric, minValue, maxValue
} from 'vuelidate/lib/validators';
import SupplierLogo from '../../../../base/components/supplier-logo/SupplierLogo';
import SupplierTariffDisplay from './SupplierTariffDisplay';
import PaymentTypeDisplay from './PaymentTypeDisplay';
import UsageDisplay from './UsageDisplay';

export default {
  name: 'Home',
  data() {
    return {
      noMpanOrMprn: false,
      form: {
        address: {}
      },
      electricityUsageConditions: { min: 1, max: 75000 },
      gasUsageConditions: { min: 1, max: 200000 },
      error: null
    };
  },
  mounted() {
    this.form = this.quote;
  },
  validations() {
    return {
      form: {
        switchType: { required },
        postcode: { required },
        address: { required },
        tariffId: {
          requiredIf: requiredIf(form => this.quote.switchType === 'dualConsolidatedToDualConsolidated')
        },
        supplierId: {
          requiredIf: requiredIf(form => this.quote.switchType === 'dualConsolidatedToDualConsolidated')
        },
        currentPayMethod: {
          requiredIf: requiredIf(form => this.quote.switchType === 'dualConsolidatedToDualConsolidated')
        },
        currentUsageEstimated: {
          requiredIf: requiredIf(form => this.quote.compareFuel !== 'gasElectricity'),
          minValue: minValue(this.quote.switchType == 'gasToGas' ? this.gasUsageConditions.min : this.electricityUsageConditions.min),
          maxValue: maxValue(this.quote.switchType == 'gasToGas' ? this.gasUsageConditions.max : this.electricityUsageConditions.max)
        },
        electricityCurrentPayMethod: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity' && this.quote.switchType !== 'dualConsolidatedToDualConsolidated')
        },
        electricitySupplierId: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity' && this.quote.switchType !== 'dualConsolidatedToDualConsolidated')
        },
        electricityTariffId: {
          requiredIf: requiredIf(form => this.quote.switchType == 'dualDistinctToDualConsolidated')
        },
        electricityCurrentUsageEstimated: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity'),
          numeric,
          minValue: this.electricityUsageConditions.min,
          maxValue: this.electricityUsageConditions.max
        },
        gasCurrentPayMethod: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity' && this.quote.switchType !== 'dualConsolidatedToDualConsolidated')
        },
        gasSupplierId: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity' && this.quote.switchType !== 'dualConsolidatedToDualConsolidated')
        },
        gasTariffId: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity' && this.quote.switchType !== 'dualConsolidatedToDualConsolidated')
        },
        gasCurrentUsageEstimated: {
          requiredIf: requiredIf(form => this.quote.compareFuel == 'gasElectricity'),
          numeric,
          minValue: this.gasUsageConditions.min,
          maxValue: this.gasUsageConditions.max
        }
      }
    };
  },
  computed: {
    ...mapState({
      appSettings: ({ app_settings }) => app_settings,
      quote: ({ quote }) => quote
    })
  },
  methods: {
    ...mapActions({
      resetList: 'lookups/resetList',
      getSuppliers: 'lookups/getSuppliers',
      goToQuestion: 'quote_questions/goToQuestion'
    }),
    onEdit(to, prop) {
      this.quote.editMode.prop = prop;

      if (prop == 'gasSupplierId') {
        this.getSuppliers({ fuelType: 'gas', paymentKey: 'gasCurrentPayMethod' });
      }

      if (prop == 'electricitySupplierId') {
        this.getSuppliers({ fuelType: 'electricity', paymentKey: 'electricityCurrentPayMethod' });
      }

      if (prop == 'supplierId') {
        this.getSuppliers({ fuelType: this.quote.compareFuel, paymentKey: 'currentPayMethod' });
      }

      this.goToQuestion(to);
    },
    onEditUsage(prop) {
      this.quote.editMode.prop = prop;
      this.goToQuestion('usage');
    },
    async onSubmit() {
      this.$v.form.$touch();

      if (this.$v.form.$invalid) {
        console.log(this.$v.form);
        for (let key in this.$v.form) {
          const element = this.$v.form[key];
          console.log(key, element['$invalid']);

        }
        return;
      }

      const error = await this.$store.dispatch('quote/createQuote');

      if (error) {
        return this.error = error;
      }

      this.$router.push({ name: 'quoteResults' });
    }
  },
  components: {
    SupplierLogo,
    SupplierTariffDisplay,
    PaymentTypeDisplay,
    UsageDisplay
  }
};
</script>

<style lang="scss" scoped>
.consumption-edit {
  position: relative;
  padding-right: 60px;
  margin-bottom: 35px;

  .btn {
    position: absolute;
    right: 5px;
    top: 10px;
  }
}
</style>
